import { Component, effect, input, output, untracked } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators, FormGroup } from '@angular/forms';
import { PhoneInput } from '../../../parts/phone-input/phone-input.component';
import { InputLabel } from '../../../parts/input-label/input-label.component';
import { EmailInput } from '../../../parts/email-input/email-input.component';
import { CustomValidators } from '../../../core/validators/custom-validators';
import { ApiResponseUtilities } from '../../../core/utilities/api-response.utilities';
import { BaseFormComponent } from '../../../core/base-classes/base-form-component';
import { OrganizationModel } from '../../../core/models/data/organization.model';
import { CreateMemberRequest, MemberModel, UpdateMemberRequest } from '../../../core/models/data/member.model';
import { MemberService } from '../../../core/services/member.service';
import { ServiceResultModel } from '../../../core/models/service/service-result.model';
import { FormState } from '../../../core/enums/forms.enums';
import { MembersMessagingConstants } from '../../../core/constants/messaging.constants';

@Component({
  selector: 'ms-member-form',
  imports: [PhoneInput, EmailInput, InputLabel, ReactiveFormsModule],
  templateUrl: './member.form.html',
})
export class MemberForm extends BaseFormComponent {

  public FormState = FormState;

  public organization = input<OrganizationModel | undefined | null>(null);
  public member = input<MemberModel | undefined>(undefined);
  public formState = input<FormState>(FormState.Create);

  public memberForm: FormGroup;

  public submitSuccess: boolean | null = null;

  public cancelRequested = output();
  public editConfirmationRequested = output();
  public submissionResult = output<ServiceResultModel>();

  constructor(private memberService: MemberService, private fb: FormBuilder) {
    super();
    this.memberForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [CustomValidators.email]],
      sendInviteEmail: [false],
      telephone: [{}, [CustomValidators.phone]]
    });

    effect(() => {
      const state = this.formState();
      const member = this.member();

      untracked(() => {
        if (state === FormState.Update && member) {
          this.populateFormForEdit();
        } else if (state === FormState.Create) {
          this.resetForm();
        }
      });
    });
  }

  public onCancel(): void {
    this.cancelRequested.emit();
  }

  public async onSubmit(): Promise<void> {
    if (this.memberForm.invalid) {
      this.markAllFieldsAsTouched();
      return;
    }

    switch (this.formState()) {
      case FormState.Create:
        await this.createMember();
        break;

      case FormState.Update:
        this.editConfirmationRequested.emit();
        break;

      default:
        break;
    }
  }

  public async createMember(): Promise<void> {
    this.isSubmitting = true;
    const createMember = {
      organizationId: this.organization()?.organizationId,
      firstName: this.memberForm.value.firstName,
      lastName: this.memberForm.value.lastName,
      emailAddress: this.memberForm.value.email,
      phoneCountryCode: this.memberForm.value.telephone?.countryCode,
      phoneNumber: this.memberForm.value.telephone?.phoneNumber,
      phoneExtension: this.memberForm.value.telephone?.extension
    } as CreateMemberRequest;

    const submitResponse = await this.memberService.createMember(createMember);

    if (submitResponse.success === true) {
      if (this.memberForm.value.sendInviteEmail === true) {
        await this.memberService.sendMemberInvitationEmail(submitResponse.data?.memberId!);
      }

      this.resetForm();
      this.submitSuccess = true;
    } else {
      this.submitSuccess = false;
      if (ApiResponseUtilities.isConflictStatusCode(submitResponse.statusCode)) {
        this.memberForm.controls['email'].setErrors({ email: { message: submitResponse.message } });
      }
    }

    this.isSubmitting = false;

    this.submissionResult.emit(submitResponse);
  }

  public async updateMember(): Promise<void> {
    this.isSubmitting = true;

    const memberData = this.member();
    if (!memberData) {
      this.submissionResult.emit({ success: false, message: MembersMessagingConstants.MEMBERS_EDIT_ERROR() });
      this.isSubmitting = false;
      return;
    }

    try {
      const updateRequest: UpdateMemberRequest = {
        firstName: this.memberForm.value.firstName,
        lastName: this.memberForm.value.lastName,
        emailAddress: this.memberForm.value.email,
        phoneCountryCode: this.memberForm.value.telephone?.countryCode,
        phoneNumber: this.memberForm.value.telephone?.phoneNumber,
        phoneExtension: this.memberForm.value.telephone?.extension,
        avatarHexColor: memberData.avatarHexColor!,
        clearAvatarFileId: false
      };

      const submitResponse = await this.memberService.updateMember(memberData.memberId, updateRequest);

      if (submitResponse.success === false && ApiResponseUtilities.isConflictStatusCode(submitResponse.statusCode)) {
        this.memberForm.controls['email'].setErrors({ email: { message: submitResponse.message } });
      }

      this.submissionResult.emit(submitResponse);
    } finally {
      this.isSubmitting = false;
    }
  }

  public populateFormForEdit(): void {
    const memberData = this.member();
    if (!memberData) return;

    this.memberForm.patchValue({
      firstName: memberData.firstName ?? '',
      lastName: memberData.lastName ?? '',
      email: memberData.emailAddress ?? '',
      sendInviteEmail: false,
      telephone: memberData.phoneNumber
        ? {
            countryCode: memberData.phoneCountryCode ?? '',
            phoneNumber: memberData.phoneNumber ?? '',
            extension: memberData.phoneExtension ?? ''
          }
        : null
    });

    this.memberForm.markAsPristine();
  }

  public resetForm(): void {
    this.memberForm.reset({
      firstName: null,
      lastName: null,
      email: null,
      sendInviteEmail: false,
      telephone: null
    });

    this.submitSuccess = null;
  }

  private markAllFieldsAsTouched(): void {
    this.memberForm.markAllAsTouched();
  }

  public getFormState(): any {
    return {
      formValue: this.memberForm.value,
      formValid: this.memberForm.valid,
      formTouched: this.memberForm.touched,
      formDirty: this.memberForm.dirty,
      emailErrors: this.memberForm.get('email')?.errors,
      telephoneErrors: this.memberForm.get('telephone')?.errors
    };
  }
}
